// FILE: src/pages/Employee/EmployeeAttendance.js
//
// FIXES APPLIED (vs previous version):
// ─────────────────────────────────────────────────────────────────
// FIX 1 — iOS autoplay: after assigning stream to videoRef,
//          explicitly call videoRef.current.play().catch(()=>{})
// FIX 2 — Camera race on iOS: stop any existing streamRef before
//          calling getUserMedia (not just on cleanup)
// FIX 3 — facingMode exact with fallback for older iPhones/iPads
// FIX 4 — aspectRatio: '4/3' replaced with fixed height (Samsung fix)
// FIX 5 — geolocation timeout bumped to 20000ms for iOS first-use
// RESPONSIVE: works on both website (desktop) and Android APK (mobile)
// ─────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Typography, Button, Paper, Divider, Stack, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Modal, Alert, CircularProgress, Avatar, Chip, LinearProgress
} from '@mui/material';
import {
    PlayArrow, Stop, EventNote, ArrowBackIos, ArrowForwardIos,
    CameraAlt, MyLocation, CheckCircle, PhotoCamera, Warning
} from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { getNativeLocation, getNativeSelfie } from '../../utils/nativeAttendance';

/* ─── helpers ─── */
const getLocalDate = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now - offset).toISOString().split('T')[0];
};

const isLateNow = () => {
    const now = new Date();
    return now.getHours() > 9 || (now.getHours() === 9 && now.getMinutes() > 40);
};

/* ─────────────────────────────────────────────────────────────
   CAMERA MODAL — browser fallback with all iOS + Android fixes
───────────────────────────────────────────────────────────── */
const CameraModal = ({ open, onCapture, onClose }) => {
    const videoRef   = useRef(null);
    const streamRef  = useRef(null);
    const [stream,    setStream]    = useState(null);
    const [error,     setError]     = useState('');
    const [capturing, setCapturing] = useState(false);

    useEffect(() => {
        if (!open) return;
        setError('');

        // FIX 2: stop any existing stream before requesting a new one
        // prevents "hardware already in use" on iOS when modal reopens fast
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }

        // FIX 3: try exact facingMode first, fall back for single-camera devices
        const tryGetCamera = (constraints) =>
            navigator.mediaDevices.getUserMedia(constraints);

        tryGetCamera({
            video: { facingMode: { exact: 'user' }, width: { ideal: 640 }, height: { ideal: 480 } },
            audio: false
        })
            .catch(() =>
                tryGetCamera({ video: { facingMode: 'user' }, audio: false })
            )
            .catch(() =>
                tryGetCamera({ video: true, audio: false })
            )
            .then(s => {
                streamRef.current = s;
                setStream(s);
                if (videoRef.current) {
                    videoRef.current.srcObject = s;
                    // FIX 1: explicitly call play() for iOS Safari
                    videoRef.current.play().catch(() => {});
                }
            })
            .catch(() => {
                setError('Camera access denied. Please allow camera permission in your browser.');
            });

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
                streamRef.current = null;
            }
        };
    }, [open]);

    const stopStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        setStream(null);
    };

    const handleCapture = () => {
        if (!videoRef.current || !stream) return;
        setCapturing(true);
        const canvas = document.createElement('canvas');
        canvas.width  = videoRef.current.videoWidth  || 640;
        canvas.height = videoRef.current.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(videoRef.current, 0, 0);
        canvas.toBlob(blob => {
            stopStream();
            setCapturing(false);
            onCapture(blob);
        }, 'image/jpeg', 0.92);
    };

    return (
        <Modal open={open} onClose={() => { stopStream(); onClose(); }}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '96vw', sm: 460 },
                bgcolor: '#0f172a', borderRadius: 3, p: { xs: 2, sm: 3 }, outline: 'none'
            }}>
                <Typography variant="h6" color="white" fontWeight={700} mb={1.5} textAlign="center" fontSize={{ xs: 16, sm: 18 }}>
                    <CameraAlt sx={{ mr: 1, verticalAlign: 'middle' }} /> Take Selfie
                </Typography>
                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                ) : (
                    <Box sx={{
                        position: 'relative', borderRadius: 2, overflow: 'hidden',
                        bgcolor: '#1e293b', mb: 2,
                        // FIX 4: fixed height instead of aspectRatio (Samsung Internet < v16 fix)
                        height: { xs: 240, sm: 320 }
                    }}>
                        <video
                            ref={videoRef} autoPlay playsInline muted
                            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
                        />
                        {!stream && (
                            <Box sx={{
                                position: 'absolute', inset: 0,
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center', gap: 1
                            }}>
                                <CircularProgress size={36} sx={{ color: 'white' }} />
                                <Typography fontSize={12} color="#94a3b8">Opening camera...</Typography>
                            </Box>
                        )}
                    </Box>
                )}
                <Stack direction="row" spacing={1.5}>
                    <Button fullWidth variant="outlined"
                        sx={{ color: 'white', borderColor: '#334155', borderRadius: 2 }}
                        onClick={() => { stopStream(); onClose(); }}>
                        Cancel
                    </Button>
                    <Button fullWidth variant="contained" color="success"
                        startIcon={capturing ? <CircularProgress size={16} color="inherit" /> : <PhotoCamera />}
                        disabled={!stream || !!error || capturing}
                        onClick={handleCapture}
                        sx={{ borderRadius: 2, fontWeight: 700 }}>
                        {capturing ? 'Saving...' : 'Take Photo'}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const EmployeeAttendance = () => {
    const { user } = useAuth();

    const [attendanceHistory,  setAttendanceHistory]  = useState([]);
    const [status,             setStatus]             = useState('OUT');
    const [loading,            setLoading]            = useState(false);
    const [lateReason,         setLateReason]         = useState('');
    const [cycleOffset,        setCycleOffset]        = useState(0);

    const [cameraOpen,         setCameraOpen]         = useState(false);
    const [selfiePreview,      setSelfiePreview]      = useState('');
    const [gpsLocation,        setGpsLocation]        = useState(null);
    const [clockAction,        setClockAction]        = useState('in');
    const [locationError,      setLocationError]      = useState('');
    const [clockError,         setClockError]         = useState('');
    const [successMsg,         setSuccessMsg]         = useState('');
    const [actionStep,         setActionStep]         = useState('');

    const todayStr = getLocalDate();

    useEffect(() => {
        return () => { if (selfiePreview) URL.revokeObjectURL(selfiePreview); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── payroll cycle ── */
    const { dateRange, cycleLabel } = useMemo(() => {
        const today = new Date();
        let baseMonth = today.getMonth();
        let baseYear  = today.getFullYear();
        if (today.getDate() < 21) baseMonth -= 1;
        baseMonth += cycleOffset;
        if (baseMonth < 0)  { baseMonth += 12; baseYear -= 1; }
        if (baseMonth > 11) { baseMonth -= 12; baseYear += 1; }
        const start = new Date(Date.UTC(baseYear, baseMonth, 21));
        const end   = new Date(Date.UTC(baseYear, baseMonth + 1, 20));
        const totalDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
        const dates = [];
        for (let i = 0; i < totalDays; i++) {
            const d = new Date(start);
            d.setUTCDate(start.getUTCDate() + i);
            dates.push(d.toISOString().split('T')[0]);
        }
        const label = `${start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - ${end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
        return { dateRange: dates, cycleLabel: label };
    }, [cycleOffset]);

    /* ── fetch ── */
    const fetchAttendance = useCallback(async () => {
        if (!user?.employeeId) return;
        setLoading(true);
        try {
            const res = await api.get(`/attendance/employee/${user.employeeId}`);
            const records = res.data || [];
            setAttendanceHistory(records);
            const todayRec = records.find(r => r.date === todayStr);
            if (todayRec) setStatus(todayRec.clockOutTime ? 'OUT' : 'IN');
            else setStatus('OUT');
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [user, todayStr]);

    useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

    /* ── STEP 1 ── */
    const startClockAction = async (action) => {
        setClockError('');
        setSuccessMsg('');
        setLocationError('');

        if (action === 'in' && isLateNow() && (!lateReason || lateReason.trim() === '')) {
            setClockError('Please enter a reason for late login before clocking in.');
            return;
        }

        setClockAction(action);
        setLoading(true);
        setActionStep('Getting your location...');

        try {
            const location = await getNativeLocation();

            if (location.isMockLocation) {
                setClockError('🚫 Fake GPS detected. Please disable mock location apps and try again.');
                setLoading(false); setActionStep('');
                return;
            }

            if (location.accuracy !== null && location.accuracy < 5) {
                setClockError('Location accuracy is suspiciously perfect. Please disable any fake GPS apps.');
                setLoading(false); setActionStep('');
                return;
            }

            setGpsLocation(location);
            setActionStep('Opening camera...');

            if (Capacitor.isNativePlatform()) {
                const blob = await getNativeSelfie();
                if (blob) {
                    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
                    setSelfiePreview(URL.createObjectURL(blob));
                    setActionStep('Submitting...');
                    await submitClockAction(blob, location);
                }
            } else {
                setLoading(false); setActionStep('');
                setCameraOpen(true);
            }

        } catch (err) {
            setLoading(false); setActionStep('');
            if (err.message?.includes('permission')) {
                setLocationError(
                    Capacitor.isNativePlatform()
                        ? 'Permission denied. Go to Settings → Apps → HRMS → Permissions → allow Location & Camera.'
                        : 'Location or camera permission denied. Please allow in browser settings.'
                );
            } else {
                setLocationError('Could not get location. Make sure GPS is turned on.');
            }
        }
    };

    /* ── STEP 2 ── */
    const handleSelfieCapture = async (blob) => {
        setCameraOpen(false);
        if (selfiePreview) URL.revokeObjectURL(selfiePreview);
        setSelfiePreview(URL.createObjectURL(blob));
        setLoading(true); setActionStep('Submitting...');
        await submitClockAction(blob, gpsLocation);
    };

    /* ── STEP 3 ── */
    const submitClockAction = async (selfieBlob, loc) => {
        setClockError('');
        try {
            const formData = new FormData();
            formData.append('selfie', selfieBlob, 'selfie.jpg');
            if (loc?.latitude != null) {
                formData.append('latitude',  String(loc.latitude));
                formData.append('longitude', String(loc.longitude));
                if (loc.accuracy != null) formData.append('gpsAccuracy', String(loc.accuracy));
                formData.append('isMockLocation', String(loc.isMockLocation || false));
            }
            if (clockAction === 'in' && isLateNow() && lateReason) formData.append('lateReason', lateReason);

            if (clockAction === 'in') {
                await api.post('/attendance/clock-in', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                setSuccessMsg('✅ Clocked in successfully!');
                setLateReason('');
            } else {
                await api.put('/attendance/clock-out', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                setSuccessMsg('✅ Clocked out successfully!');
            }
            fetchAttendance();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || '';
            setClockError(msg || (clockAction === 'in' ? 'Clock-in failed. Please try again.' : 'Clock-out failed.'));
            if (selfiePreview) URL.revokeObjectURL(selfiePreview);
            setSelfiePreview('');
        } finally {
            setLoading(false); setActionStep('');
        }
    };

    /* ── status indicator ── */
    const getIndicator = (date) => {
        const rec = attendanceHistory.find(r => r.date === date);
        if (rec?.displayStatus) {
            switch (rec.displayStatus) {
                case 'P':   return { label: 'P', color: '#16a34a' };
                case 'A':   return { label: 'A', color: '#dc2626' };
                case 'W': case 'W/O': return { label: 'W', color: '#ca8a04' };
                case 'H':   return { label: 'H', color: '#4f46e5' };
                case 'L':   return { label: 'L', color: '#db2777' };
                default:    break;
            }
        }
        const d = new Date(date + 'T00:00:00');
        const today = new Date(todayStr + 'T00:00:00');
        if (d.getDay() === 0) return { label: 'W', color: '#ca8a04' };
        if (d < today)        return { label: 'A', color: '#dc2626' };
        return { label: '-', color: '#94a3b8' };
    };

    const employeeName = attendanceHistory.length > 0 ? attendanceHistory[0].employeeName : 'EMPLOYEE';
    const liveMapUrl   = gpsLocation?.latitude
        ? `https://www.google.com/maps?q=${gpsLocation.latitude},${gpsLocation.longitude}` : null;

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ fontSize: { xs: 20, sm: 24 } }}>
                Attendance
            </Typography>

            {/* ── CLOCK PANEL ── */}
            <Paper sx={{ p: { xs: 2.5, sm: 3 }, mb: 3, borderRadius: 3 }}>

                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                    <Box>
                        <Typography fontSize={{ xs: 11, sm: 13 }} color="text.secondary">Today</Typography>
                        <Typography fontWeight={800} fontSize={{ xs: 16, sm: 18 }}>{todayStr}</Typography>
                    </Box>
                    <Chip
                        label={status === 'IN' ? '🟢 Clocked In' : '🔴 Not Clocked In'}
                        color={status === 'IN' ? 'success' : 'default'}
                        sx={{ fontWeight: 700, fontSize: { xs: 11, sm: 13 } }}
                    />
                </Stack>

                {loading && actionStep && (
                    <Box mb={2}>
                        <Typography fontSize={11} color="text.secondary" mb={0.5}>{actionStep}</Typography>
                        <LinearProgress sx={{ borderRadius: 2, height: 4 }} />
                    </Box>
                )}

                {gpsLocation?.latitude && (
                    <Stack direction="row" alignItems="center" spacing={0.5} mb={1.5}
                        component="a" href={liveMapUrl} target="_blank" rel="noopener noreferrer"
                        sx={{ textDecoration: 'none', cursor: 'pointer' }}>
                        <MyLocation sx={{ fontSize: 14, color: '#16a34a' }} />
                        <Typography fontSize={{ xs: 11, sm: 12 }} color="#16a34a" sx={{ textDecoration: 'underline' }}>
                            📍 Location acquired ({gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}) — tap to verify
                        </Typography>
                    </Stack>
                )}

                {locationError && (
                    <Alert severity="warning" icon={<Warning fontSize="small" />}
                        onClose={() => setLocationError('')}
                        sx={{ mb: 2, borderRadius: 2, fontSize: { xs: 12, sm: 13 } }}>
                        {locationError}
                    </Alert>
                )}
                {clockError && (
                    <Alert severity="error" onClose={() => setClockError('')}
                        sx={{ mb: 2, borderRadius: 2, fontSize: { xs: 12, sm: 13 } }}>
                        {clockError}
                    </Alert>
                )}
                {successMsg && (
                    <Alert severity="success" onClose={() => setSuccessMsg('')}
                        sx={{ mb: 2, borderRadius: 2, fontSize: { xs: 12, sm: 13 } }}>
                        {successMsg}
                    </Alert>
                )}

                {isLateNow() && status === 'OUT' && (
                    <Box mb={2}>
                        <Typography fontSize={{ xs: 12, sm: 13 }} mb={0.5} color="error.main" fontWeight={600}>
                            ⏰ You are late — reason required:
                        </Typography>
                        <TextField
                            fullWidth size="small"
                            value={lateReason}
                            onChange={e => setLateReason(e.target.value)}
                            placeholder="e.g. Traffic, Doctor visit..."
                            sx={{ '& .MuiInputBase-input': { fontSize: { xs: 13, sm: 14 } } }}
                        />
                    </Box>
                )}

                {selfiePreview && (
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={2}
                        sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                        <Avatar src={selfiePreview} sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, border: '2px solid #16a34a' }} />
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CheckCircle sx={{ fontSize: 14, color: '#16a34a' }} />
                                <Typography fontSize={{ xs: 12, sm: 13 }} color="#16a34a" fontWeight={700}>Selfie captured</Typography>
                            </Stack>
                            <Typography fontSize={11} color="text.secondary">Visible to your Approval Manager only</Typography>
                        </Box>
                    </Stack>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={1}>
                    <Button
                        fullWidth variant="contained" color="success" size="large"
                        startIcon={loading && clockAction === 'in'
                            ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
                        disabled={status === 'IN' || loading}
                        onClick={() => startClockAction('in')}
                        sx={{ borderRadius: 2, fontWeight: 700, py: { xs: 1.5, sm: 1.8 }, fontSize: { xs: 14, sm: 15 } }}>
                        Clock In
                    </Button>
                    <Button
                        fullWidth variant="contained" color="error" size="large"
                        startIcon={loading && clockAction === 'out'
                            ? <CircularProgress size={18} color="inherit" /> : <Stop />}
                        disabled={status === 'OUT' || loading}
                        onClick={() => startClockAction('out')}
                        sx={{ borderRadius: 2, fontWeight: 700, py: { xs: 1.5, sm: 1.8 }, fontSize: { xs: 14, sm: 15 } }}>
                        Clock Out
                    </Button>
                </Stack>

                <Typography fontSize={11} color="text.secondary" mt={1.5} textAlign="center">
                    {Capacitor.isNativePlatform()
                        ? '📍 GPS verified · 📸 Selfie required · 🔒 Fake GPS blocked automatically'
                        : '📍 Location and selfie are required for clock in/out'}
                </Typography>
            </Paper>

            <Divider sx={{ mb: 2 }} />

            {/* ── ATTENDANCE MATRIX ── */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={2} gap={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <EventNote />
                    <Typography variant="h6" fontWeight={700} fontSize={{ xs: 15, sm: 17 }}>Attendance Matrix</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <IconButton size="small" onClick={() => setCycleOffset(cycleOffset - 1)}>
                        <ArrowBackIos fontSize="small" />
                    </IconButton>
                    <Typography fontWeight={700} fontSize={{ xs: 12, sm: 14 }}>{cycleLabel}</Typography>
                    <IconButton size="small" disabled={cycleOffset >= 0} onClick={() => setCycleOffset(cycleOffset + 1)}>
                        <ArrowForwardIos fontSize="small" />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: { xs: 11, sm: 13 }, whiteSpace: 'nowrap' }}>Employee</TableCell>
                            {dateRange.map(d => (
                                <TableCell key={d} align="center" sx={{ fontSize: { xs: 10, sm: 12 }, p: { xs: '4px 2px', sm: '6px 8px' } }}>
                                    {d.slice(8, 10)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: { xs: 11, sm: 13 }, whiteSpace: 'nowrap' }}>
                                {employeeName}
                            </TableCell>
                            {dateRange.map(d => {
                                const i = getIndicator(d);
                                return (
                                    <TableCell key={d} align="center" sx={{ p: { xs: '4px 2px', sm: '6px 8px' } }}>
                                        <Typography fontWeight={800} color={i.color} fontSize={{ xs: 10, sm: 12 }}>
                                            {i.label}
                                        </Typography>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <CameraModal
                open={cameraOpen}
                onCapture={handleSelfieCapture}
                onClose={() => setCameraOpen(false)}
            />
        </Box>
    );
};

export default EmployeeAttendance;